<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">
            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Patient Profile</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript: void(0);">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript: void(0);" @click="goTo('patient-list')">Patients</a></li>
                      <li class="breadcrumb-item active">Profile</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3"></div>
              <p class="text-muted">Loading patient profile...</p>
            </div>

            <!-- Not Found -->
            <div v-else-if="!patient" class="text-center py-5">
              <div class="avatar-lg mx-auto mb-4">
                <span class="avatar-title bg-light text-muted rounded-circle"><i class="mdi mdi-account-off font-size-48"></i></span>
              </div>
              <h5 class="text-muted">Patient not found</h5>
              <button class="btn btn-primary mt-3" @click="$router.go(-1)"><i class="mdi mdi-arrow-left me-1"></i> Go Back</button>
            </div>

            <!-- Profile -->
            <div v-else>
              <!-- Header Card -->
              <div class="card mb-4">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <div class="d-flex align-items-center">
                        <div class="avatar-lg me-4">
                          <span class="avatar-title rounded-circle font-size-24" :class="patient.gender === 'Male' ? 'bg-primary' : 'bg-pink'">{{ getInitials(patient.fullNames) }}</span>
                        </div>
                        <div>
                          <h4 class="mb-1">{{ patient.fullNames }}</h4>
                          <div class="d-flex flex-wrap gap-2 mb-2">
                            <span class="badge bg-secondary"><i class="mdi mdi-pound me-1"></i>{{ patient.patientID }}</span>
                            <span class="badge" :class="patient.gender === 'Male' ? 'bg-primary' : 'bg-pink'"><i class="mdi mdi-gender-male-female me-1"></i>{{ patient.gender }}</span>
                            <span class="badge bg-info"><i class="mdi mdi-calendar me-1"></i>{{ patient.age }} years ({{ patient.ageGroup }})</span>
                            <span v-if="patient.bloodGroup" class="badge bg-danger"><i class="mdi mdi-water me-1"></i>{{ patient.bloodGroup }}</span>
                            <span class="badge" :class="{'bg-success': patient.status === 'Active', 'bg-secondary': patient.status === 'Inactive', 'bg-dark': patient.status === 'Deceased'}">{{ patient.status }}</span>
                          </div>
                          <div class="text-muted small">
                            <span v-if="patient.phoneNumber" class="me-3"><i class="mdi mdi-phone me-1"></i>{{ patient.phoneNumber }}</span>
                            <span v-if="patient.email" class="me-3"><i class="mdi mdi-email me-1"></i>{{ patient.email }}</span>
                            <span v-if="patient.idNo"><i class="mdi mdi-card-account-details me-1"></i>ID: {{ patient.idNo }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4 text-md-end mt-3 mt-md-0">
                      <button class="btn btn-success btn-sm me-2" @click="createVisit"><i class="mdi mdi-calendar-plus me-1"></i> New Visit</button>
                      <button class="btn btn-primary btn-sm me-2" @click="editPatient"><i class="mdi mdi-pencil me-1"></i> Edit</button>
                      <button class="btn btn-outline-secondary btn-sm" @click="printProfile"><i class="mdi mdi-printer me-1"></i> Print</button>
                    </div>
                  </div>

                  <!-- Medical Alerts -->
                  <div v-if="patient.allergies || patient.chronicConditions" class="alert alert-warning mt-3 mb-0">
                    <div class="d-flex align-items-start">
                      <i class="mdi mdi-alert-circle font-size-20 me-2"></i>
                      <div>
                        <strong>Medical Alerts</strong>
                        <div v-if="patient.allergies" class="mt-1"><strong>Allergies:</strong> {{ patient.allergies }}</div>
                        <div v-if="patient.chronicConditions" class="mt-1"><strong>Chronic Conditions:</strong> {{ patient.chronicConditions }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tabs -->
              <ul class="nav nav-tabs nav-tabs-custom mb-4">
                <li class="nav-item"><a class="nav-link" :class="{ active: activeTab === 'overview' }" href="javascript: void(0);" @click.prevent="activeTab = 'overview'"><i class="mdi mdi-account-details me-1"></i> Overview</a></li>
                <li class="nav-item"><a class="nav-link" :class="{ active: activeTab === 'visits' }" href="javascript: void(0);" @click.prevent="activeTab = 'visits'; loadVisits()"><i class="mdi mdi-calendar-check me-1"></i> Visits <span v-if="visitCount" class="badge bg-primary ms-1">{{ visitCount }}</span></a></li>
                <li class="nav-item"><a class="nav-link" :class="{ active: activeTab === 'insurance' }" href="javascript: void(0);" @click.prevent="activeTab = 'insurance'; loadInsurance()"><i class="mdi mdi-shield-check me-1"></i> Insurance</a></li>
                <li class="nav-item"><a class="nav-link" :class="{ active: activeTab === 'billing' }" href="javascript: void(0);" @click.prevent="activeTab = 'billing'; loadBillingHistory()"><i class="mdi mdi-receipt me-1"></i> Billing</a></li>
              </ul>

              <!-- Tab Content -->
              <!-- Overview Tab -->
              <div v-show="activeTab === 'overview'">
                <div class="row g-4">
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-header"><h6 class="mb-0"><i class="mdi mdi-account me-2"></i>Personal Information</h6></div>
                      <div class="card-body">
                        <table class="table table-sm table-borderless mb-0">
                          <tr><td class="text-muted" width="40%">Full Name</td><td><strong>{{ patient.fullNames }}</strong></td></tr>
                          <tr><td class="text-muted">Date of Birth</td><td>{{ formatDate(patient.dateOfBirth) }} ({{ patient.age }} years)</td></tr>
                          <tr><td class="text-muted">Gender</td><td>{{ patient.gender }}</td></tr>
                          <tr><td class="text-muted">Blood Group</td><td>{{ patient.bloodGroup || '-' }}</td></tr>
                          <tr><td class="text-muted">Marital Status</td><td>{{ patient.maritalStatus || '-' }}</td></tr>
                          <tr><td class="text-muted">Occupation</td><td>{{ patient.occupation || '-' }}</td></tr>
                          <tr><td class="text-muted">Nationality</td><td>{{ patient.nationality || '-' }}</td></tr>
                          <tr><td class="text-muted">Religion</td><td>{{ patient.religion || '-' }}</td></tr>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-header"><h6 class="mb-0"><i class="mdi mdi-phone me-2"></i>Contact Information</h6></div>
                      <div class="card-body">
                        <table class="table table-sm table-borderless mb-0">
                          <tr><td class="text-muted" width="40%">Phone</td><td><a v-if="patient.phoneNumber" :href="'tel:' + patient.phoneNumber">{{ patient.phoneNumber }}</a><span v-else>-</span></td></tr>
                          <tr><td class="text-muted">Alt. Phone</td><td>{{ patient.alternativePhone || '-' }}</td></tr>
                          <tr><td class="text-muted">Email</td><td><a v-if="patient.email" :href="'mailto:' + patient.email">{{ patient.email }}</a><span v-else>-</span></td></tr>
                          <tr><td class="text-muted">Address</td><td>{{ patient.physicalAddress || '-' }}</td></tr>
                          <tr><td class="text-muted">Postal Address</td><td>{{ patient.postalAddress || '-' }}</td></tr>
                          <tr><td class="text-muted">County</td><td>{{ patient.county || '-' }}</td></tr>
                          <tr><td class="text-muted">Sub-County</td><td>{{ patient.subCountyName || '-' }}</td></tr>
                          <tr><td class="text-muted">Village</td><td>{{ patient.village || '-' }}</td></tr>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-header"><h6 class="mb-0"><i class="mdi mdi-card-account-details me-2"></i>Identification</h6></div>
                      <div class="card-body">
                        <table class="table table-sm table-borderless mb-0">
                          <tr><td class="text-muted" width="40%">Patient ID</td><td><code class="text-primary">{{ patient.patientID }}</code></td></tr>
                          <tr><td class="text-muted">National ID</td><td>{{ patient.idNo || '-' }}</td></tr>
                          <tr><td class="text-muted">Passport No.</td><td>{{ patient.passportNo || '-' }}</td></tr>
                          <tr><td class="text-muted">Birth Cert No.</td><td>{{ patient.birthCertNo || '-' }}</td></tr>
                          <tr><td class="text-muted">Huduma Namba</td><td>{{ patient.hudumaNamba || '-' }}</td></tr>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-header"><h6 class="mb-0"><i class="mdi mdi-account-group me-2"></i>Next of Kin / Emergency</h6></div>
                      <div class="card-body">
                        <h6 class="text-primary small mb-2">Next of Kin</h6>
                        <table class="table table-sm table-borderless mb-3">
                          <tr><td class="text-muted" width="40%">Name</td><td>{{ patient.nextOfKinName || '-' }}</td></tr>
                          <tr><td class="text-muted">Relationship</td><td>{{ patient.nextOfKinRelationship || '-' }}</td></tr>
                          <tr><td class="text-muted">Phone</td><td><a v-if="patient.nextOfKinPhone" :href="'tel:' + patient.nextOfKinPhone">{{ patient.nextOfKinPhone }}</a><span v-else>-</span></td></tr>
                        </table>
                        <h6 class="text-primary small mb-2">Emergency Contact</h6>
                        <table class="table table-sm table-borderless mb-0">
                          <tr><td class="text-muted" width="40%">Name</td><td>{{ patient.emergencyContactName || '-' }}</td></tr>
                          <tr><td class="text-muted">Phone</td><td><a v-if="patient.emergencyContactPhone" :href="'tel:' + patient.emergencyContactPhone">{{ patient.emergencyContactPhone }}</a><span v-else>-</span></td></tr>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="card">
                      <div class="card-header"><h6 class="mb-0"><i class="mdi mdi-heart-pulse me-2"></i>Medical History</h6></div>
                      <div class="card-body">
                        <div class="row">
                          <div class="col-md-4"><h6 class="text-muted small">Allergies</h6><p :class="patient.allergies ? 'text-danger' : 'text-muted'">{{ patient.allergies || 'None recorded' }}</p></div>
                          <div class="col-md-4"><h6 class="text-muted small">Chronic Conditions</h6><p :class="patient.chronicConditions ? 'text-warning' : 'text-muted'">{{ patient.chronicConditions || 'None recorded' }}</p></div>
                          <div class="col-md-4"><h6 class="text-muted small">Disabilities</h6><p>{{ patient.disabilities || 'None recorded' }}</p></div>
                          <div class="col-md-4"><h6 class="text-muted small">Current Medications</h6><p>{{ patient.currentMedications || 'None recorded' }}</p></div>
                          <div class="col-md-4"><h6 class="text-muted small">Previous Surgeries</h6><p>{{ patient.previousSurgeries || 'None recorded' }}</p></div>
                          <div class="col-md-4"><h6 class="text-muted small">Family History</h6><p>{{ patient.familyHistory || 'None recorded' }}</p></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="card bg-light">
                      <div class="card-body py-2">
                        <div class="row small text-muted">
                          <div class="col-md-4"><i class="mdi mdi-hospital-building me-1"></i>Registered at: <strong>{{ patient.registrationFacilityName || '-' }}</strong></div>
                          <div class="col-md-4"><i class="mdi mdi-calendar me-1"></i>Registered: <strong>{{ formatDate(patient.registrationDate || patient.dateCreated) }}</strong></div>
                          <div class="col-md-4"><i class="mdi mdi-account me-1"></i>By: <strong>{{ patient.createdBy || '-' }}</strong></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Visits Tab -->
              <div v-show="activeTab === 'visits'">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Visit History</h6>
                    <button class="btn btn-sm btn-success" @click="createVisit"><i class="mdi mdi-plus me-1"></i> New Visit</button>
                  </div>
                  <div class="card-body p-0">
                    <div v-if="loadingVisits" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></div>
                    <div v-else-if="visits.length === 0" class="text-center py-4 text-muted"><i class="mdi mdi-calendar-remove font-size-24 d-block mb-2"></i>No visits recorded</div>
                    <div v-else class="table-responsive">
                      <table class="table table-hover mb-0">
                        <thead class="table-light">
                        <tr><th>Visit ID</th><th>Date</th><th>Type</th><th>Department</th><th>Doctor</th><th>Status</th><th>Action</th></tr>
                        </thead>
                        <tbody>
                        <tr v-for="visit in visits" :key="visit.visitID">
                          <td><code>{{ visit.visitID }}</code></td>
                          <td>{{ formatDate(visit.visitDate) }}</td>
                          <td>{{ visit.visitType }}</td>
                          <td>{{ visit.departmentName || '-' }}</td>
                          <td>{{ visit.doctorName || '-' }}</td>
                          <td><span class="badge" :class="{'bg-success': visit.status === 'Completed', 'bg-warning': visit.status === 'Active', 'bg-secondary': visit.status === 'Cancelled'}">{{ visit.status }}</span></td>
                          <td><button class="btn btn-sm btn-soft-primary" @click="viewVisit(visit)"><i class="mdi mdi-eye"></i></button></td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Insurance Tab -->
              <div v-show="activeTab === 'insurance'">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Insurance Information</h6>
                    <button class="btn btn-sm btn-primary" @click="goToInsurance"><i class="mdi mdi-plus me-1"></i> Add Insurance</button>
                  </div>
                  <div class="card-body">
                    <div v-if="loadingInsurance" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></div>
                    <div v-else-if="insurances.length === 0" class="text-center py-4 text-muted"><i class="mdi mdi-shield-off font-size-24 d-block mb-2"></i>No insurance records</div>
                    <div v-else class="row g-3">
                      <div v-for="ins in insurances" :key="ins.insuranceID" class="col-md-6">
                        <div class="card insurance-card" :class="{ 'border-primary': ins.isPrimary === 'Yes' }" @click="goToInsurance" style="cursor: pointer;">
                          <div class="card-body">
                            <div class="d-flex justify-content-between align-items-start">
                              <div><h6 class="mb-1">{{ ins.insuranceProvider }} <span v-if="ins.isPrimary === 'Yes'" class="badge bg-primary ms-1">Primary</span></h6><small class="text-muted">{{ ins.scheme || ins.insuranceType }}</small></div>
                              <span class="badge" :class="{'bg-success': ins.status === 'Active', 'bg-secondary': ins.status !== 'Active'}">{{ ins.status }}</span>
                            </div>
                            <hr class="my-2">
                            <p class="mb-1 small"><strong>Member No:</strong> {{ ins.memberNumber }}</p>
                            <p class="mb-1 small"><strong>Valid:</strong> {{ formatDate(ins.validFrom) }} - {{ formatDate(ins.validTo) }}</p>
                            <p class="mb-0 small"><strong>Balance:</strong> <span class="text-success">KES {{ numberFormat(ins.balanceAmount) }}</span></p>
                            <div class="text-end mt-2"><small class="text-primary">View Details <i class="mdi mdi-arrow-right"></i></small></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Billing Tab -->
              <div v-show="activeTab === 'billing'">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h6 class="mb-0"><i class="mdi mdi-receipt me-2"></i>Billing History</h6>
                    <button class="btn btn-sm btn-outline-primary" @click="loadBillingHistory" :disabled="loadingBilling">
                      <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loadingBilling }"></i> Refresh
                    </button>
                  </div>
                  <div class="card-body">
                    <!-- Loading -->
                    <div v-if="loadingBilling" class="text-center py-4">
                      <div class="spinner-border spinner-border-sm text-primary"></div>
                      <p class="text-muted small mt-2 mb-0">Loading billing data...</p>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="billingHistory.length === 0" class="text-center py-4 text-muted">
                      <i class="mdi mdi-receipt-text-outline font-size-48 d-block mb-3"></i>
                      <h6>No billing records</h6>
                      <p class="small">Billing records will appear here after visits</p>
                    </div>

                    <!-- Billing Content -->
                    <div v-else>
                      <!-- Summary Cards -->
                      <div class="row g-3 mb-4">
                        <div class="col-md-3">
                          <div class="card bg-primary text-white mb-0">
                            <div class="card-body py-3 text-center">
                              <small class="d-block opacity-75">Total Charges</small>
                              <h4 class="mb-0 text-white">KES {{ numberFormat(billingSummary.totalCharges) }}</h4>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-3">
                          <div class="card bg-success text-white mb-0">
                            <div class="card-body py-3 text-center">
                              <small class="d-block opacity-75">Billed</small>
                              <h4 class="mb-0 text-white">KES {{ numberFormat(billingSummary.totalBilled) }}</h4>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-3">
                          <div class="card bg-warning text-dark mb-0">
                            <div class="card-body py-3 text-center">
                              <small class="d-block opacity-75 text-white">Unbilled</small>
                              <h4 class="mb-0 text-white">KES {{ numberFormat(billingSummary.totalUnbilled) }}</h4>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-3">
                          <div class="card bg-info text-white mb-0">
                            <div class="card-body py-3 text-center">
                              <small class="d-block opacity-75">Invoices</small>
                              <h4 class="mb-0 text-white">{{ billingSummary.invoiceCount }}</h4>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Invoices List -->
                      <div v-if="invoices.length > 0" class="mb-4">
                        <h6 class="text-muted mb-3"><i class="mdi mdi-file-document-multiple me-1"></i>Generated Invoices</h6>
                        <div class="table-responsive">
                          <table class="table table-hover mb-0">
                            <thead class="table-light">
                            <tr>
                              <th>Invoice No</th>
                              <th>Date</th>
                              <th>Visit</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="inv in invoices" :key="inv.billNo">
                              <td><strong class="text-success">{{ inv.billNo }}</strong></td>
                              <td>{{ formatDate(inv.billedAt || inv.dateCreated) }}</td>
                              <td><code class="small">{{ inv.visitID }}</code></td>
                              <td>KES {{ numberFormat(inv.amount) }}</td>
                              <td>
                                    <span class="badge" :class="inv.status === 'paid' ? 'bg-success' : 'bg-warning'">
                                      {{ inv.status === 'paid' ? 'Paid' : 'Unpaid' }}
                                    </span>
                              </td>
                              <td>
                                <button class="btn btn-sm btn-soft-primary me-1" @click="viewBill(inv.billNo)" title="View">
                                  <i class="mdi mdi-eye"></i>
                                </button>
                                <button class="btn btn-sm btn-soft-success" @click="printBill(inv.billNo)" title="Print">
                                  <i class="mdi mdi-printer"></i>
                                </button>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Charges by Visit -->
                      <div>
                        <h6 class="text-muted mb-3"><i class="mdi mdi-format-list-bulleted me-1"></i>Charges by Visit</h6>
                        <div class="accordion" id="chargesAccordion">
                          <div v-for="(visitData, index) in billingHistory" :key="visitData.visitID" class="accordion-item">
                            <h2 class="accordion-header">
                              <button class="accordion-button" :class="{ collapsed: index !== 0 }" type="button"
                                      data-bs-toggle="collapse" :data-bs-target="'#collapse' + index">
                                <div class="d-flex justify-content-between align-items-center w-100 me-3">
                                  <div>
                                    <strong>{{ visitData.visitID }}</strong>
                                    <span class="badge bg-secondary ms-2">{{ visitData.visitType }}</span>
                                    <small class="text-muted ms-2">{{ formatDate(visitData.visitDate) }}</small>
                                  </div>
                                  <div>
                                    <span class="badge bg-primary me-2">{{ visitData.chargeCount }} items</span>
                                    <strong>KES {{ numberFormat(visitData.totalAmount) }}</strong>
                                  </div>
                                </div>
                              </button>
                            </h2>
                            <div :id="'collapse' + index" class="accordion-collapse collapse" :class="{ show: index === 0 }" data-bs-parent="#chargesAccordion">
                              <div class="accordion-body p-0">
                                <table class="table table-sm mb-0">
                                  <thead class="table-light">
                                  <tr>
                                    <th>Service</th>
                                    <th>Category</th>
                                    <th class="text-end">Amount</th>
                                    <th class="text-center">Status</th>
                                    <th>Bill No</th>
                                  </tr>
                                  </thead>
                                  <tbody>
                                  <tr v-for="charge in visitData.charges" :key="charge.visitChargeID">
                                    <td>{{ charge.serviceName }}</td>
                                    <td><span class="badge bg-light text-dark">{{ charge.serviceCategory }}</span></td>
                                    <td class="text-end">KES {{ numberFormat(charge.netAmount) }}</td>
                                    <td class="text-center">
                                          <span class="badge" :class="charge.billed === 'Yes' ? 'bg-success' : 'bg-warning'">
                                            {{ charge.billed === 'Yes' ? 'Billed' : 'Pending' }}
                                          </span>
                                    </td>
                                    <td>
                                      <a v-if="charge.billNo" href="javascript:void(0);" @click="viewBill(charge.billNo)" class="text-success small">
                                        {{ charge.billNo }}
                                      </a>
                                      <span v-else class="text-muted">-</span>
                                    </td>
                                  </tr>
                                  </tbody>
                                  <tfoot class="table-light">
                                  <tr>
                                    <td colspan="2"><strong>Total</strong></td>
                                    <td class="text-end"><strong>KES {{ numberFormat(visitData.totalAmount) }}</strong></td>
                                    <td colspan="2"></td>
                                  </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6"><div class="text-sm-end d-sm-block">County Hospital Management System</div></div>
            </div>
          </div>
        </footer>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { execute, hospital } from "@/api";

export default {
  name: "PatientProfile",
  components: { NavigationBar },
  props: { id: { type: String, required: true } },

  data() {
    return {
      loading: false,
      patient: null,
      activeTab: 'overview',
      visits: [],
      visitCount: 0,
      loadingVisits: false,
      insurances: [],
      loadingInsurance: false,
      // Billing
      loadingBilling: false,
      billingHistory: [],
      invoices: [],
      billingSummary: {
        totalCharges: 0,
        totalBilled: 0,
        totalUnbilled: 0,
        invoiceCount: 0
      }
    };
  },

  mounted() { this.loadPatient(); },
  watch: { id() { this.loadPatient(); } },

  methods: {
    goTo(route) { this.$router.push('/' + route); },
    numberFormat(num) { return new Intl.NumberFormat("en-GB").format(num || 0); },

    loadPatient() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "searchPatient");
      data.append("patientID", this.id);
      data.append("singleResult", "true");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, hospital).then((res) => {
        this.loading = false;
        if (res.data.success && res.data.data.patient) {
          this.patient = res.data.data.patient;
          this.loadVisitCount();
        } else this.patient = null;
      }).catch((e) => { this.loading = false; this.patient = null; console.error('Error:', e); });
    },

    loadVisitCount() {
      const data = new FormData();
      data.append("function", "getPatientHistory");
      data.append("patientID", this.id);
      data.append("limit", 1);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => {
        if (res.data.success) this.visitCount = res.data.data.totalVisits || 0;
      }).catch(e => console.error('Error:', e));
    },

    loadVisits() {
      if (this.visits.length > 0) return;
      this.loadingVisits = true;
      const data = new FormData();
      data.append("function", "getPatientHistory");
      data.append("patientID", this.id);
      data.append("limit", 50);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => {
        this.loadingVisits = false;
        if (res.data.success) this.visits = res.data.data.visits || [];
      }).catch((e) => { this.loadingVisits = false; console.error('Error:', e); });
    },

    loadInsurance() {
      if (this.insurances.length > 0) return;
      this.loadingInsurance = true;
      const data = new FormData();
      data.append("function", "getPatientInsurance");
      data.append("patientID", this.id);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => {
        this.loadingInsurance = false;
        if (res.data.success) this.insurances = res.data.data.insurances || [];
      }).catch((e) => { this.loadingInsurance = false; console.error('Error:', e); });
    },

    createVisit() { this.$router.push('/new-visit?patientID=' + this.id); },
    editPatient() { this.$router.push('/patient-edit/' + this.id); },
    viewVisit(visit) { this.$router.push('/visit-details/' + visit.visitID); },
    goToInsurance() { this.$router.push('/patient-insurance/' + this.id); },
    printProfile() { window.print(); },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
    },

    // ==========================================
    // BILLING METHODS
    // ==========================================

    loadBillingHistory() {
      // Only load once unless forced
      if (this.billingHistory.length > 0) return;

      this.loadingBilling = true;

      const data = new FormData();
      data.append("function", "getPatientBillingHistory");
      data.append("patientID", this.id);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, hospital).then((res) => {
        this.loadingBilling = false;
        if (res.data.success) {
          const responseData = res.data.data || {};
          this.billingHistory = responseData.visits || [];
          this.invoices = responseData.invoices || [];
          this.billingSummary = responseData.summary || {
            totalCharges: 0,
            totalBilled: 0,
            totalUnbilled: 0,
            invoiceCount: 0
          };
        }
      }).catch((e) => {
        this.loadingBilling = false;
        console.error('Error loading billing:', e);
      });
    },

    viewBill(billNo) {
      if (!billNo) return;
      localStorage['params'] = JSON.stringify({ billNo: billNo });
      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    },

    printBill(billNo) {
      if (!billNo) return;
      localStorage['params'] = JSON.stringify({ billNo: billNo });
      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    }
  }
}
</script>

<style scoped>
.avatar-lg { width: 5rem; height: 5rem; }
.avatar-title { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: #fff; font-weight: 600; }
.bg-pink { background-color: #e83e8c !important; }
.nav-tabs-custom .nav-link { color: #495057; }
.nav-tabs-custom .nav-link.active { color: #556ee6; border-bottom: 2px solid #556ee6; }
.btn-soft-primary { background-color: rgba(85, 110, 230, 0.1); color: #556ee6; border: none; }
.btn-soft-primary:hover { background-color: #556ee6; color: #fff; }
.btn-soft-success { background-color: rgba(52, 195, 143, 0.1); color: #34c38f; border: none; }
.btn-soft-success:hover { background-color: #34c38f; color: #fff; }
.insurance-card { transition: all 0.2s ease; }
.insurance-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(85, 110, 230, 0.15); border-color: #556ee6 !important; }
.accordion-button:not(.collapsed) { background-color: rgba(85, 110, 230, 0.1); color: #556ee6; }
.accordion-button:focus { box-shadow: none; border-color: rgba(85, 110, 230, 0.2); }
@media print { .btn, .nav-tabs, .card-header button { display: none !important; } }
</style>